import React, { useRef, useState, useEffect } from "react";
import PwaPromoBanner from "@/components/PwaPromoBanner";
import {
  ShieldCheck,
  MapPin,
  Zap,
  Users,
  Building2,
  TrendingUp,
  Star,
  CheckCircle,
  ArrowRight,
  Globe,
} from "lucide-react";

/* ── tiny hook: animate a section into view on scroll (same pattern as the Contact page) ── */
function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.12 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, visible };
}

const FEATURES = [
  {
    icon: <ShieldCheck className="w-6 h-6" />,
    title: "100% Verified Jobs",
    desc: "Every listing is manually reviewed by our admin team before going live — no spam, no scams, only real opportunities.",
  },
  {
    icon: <MapPin className="w-6 h-6" />,
    title: "City-First Discovery",
    desc: "Built for India's diversity. Browse by your city — from Surat's textile hubs to Bengaluru's IT corridors.",
  },
  {
    icon: <Zap className="w-6 h-6" />,
    title: "One-Click Apply",
    desc: "No lengthy forms, no sign-up walls. Job seekers apply instantly and employers hear back fast.",
  },
];

const STATS = [
  { value: "100+", label: "Active Jobs", icon: <TrendingUp className="w-5 h-5" /> },
  { value: "2+", label: "Cities Covered", icon: <Globe className="w-5 h-5" /> },
  { value: "1.2M+", label: "Job Seekers", icon: <Users className="w-5 h-5" /> },
  { value: "9000+", label: "Employers", icon: <Building2 className="w-5 h-5" /> },
];

const STORY_POINTS = [
  "Started in 2022 to solve local job discovery in Tier-2 and Tier-3 cities",
  "Trusted by shops, factories, startups, and Fortune-500 offices alike",
  "Every job verified by a real human before candidates ever see it",
  "Completely free — no hidden fees, ever, for seekers or posters",
];

const TEAM = [
  { initials: "RK", name: "Rohan Kapur", role: "Co-founder & CEO" },
  { initials: "PM", name: "Priya Menon", role: "Co-founder & CTO" },
  { initials: "AS", name: "Aryan Shah", role: "Head of Operations" },
];

export default function About() {
  const statsReveal = useReveal();
  const featuresReveal = useReveal();
  const storyReveal = useReveal();
  const ctaReveal = useReveal();

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400;12..96,600;12..96,800&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;1,9..40,300&display=swap');

        .about-root { font-family: 'DM Sans', sans-serif; background: #f9f9f9; }

        /* HERO */
        .about-hero {
          background: #fff;
          border-bottom: 1px solid #e5e5e5;
          padding: 72px 24px 80px;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
        .about-hero-dot-grid {
          position: absolute; inset: 0;
          background-image: radial-gradient(rgba(0,0,0,0.05) 1px, transparent 1px);
          background-size: 26px 26px;
          -webkit-mask-image: radial-gradient(ellipse 70% 55% at 50% 0%, #000 0%, transparent 75%);
          mask-image: radial-gradient(ellipse 70% 55% at 50% 0%, #000 0%, transparent 75%);
          pointer-events: none;
        }
        .about-hero > * { position: relative; z-index: 1; }

        /* REVEAL ANIMATION */
        .reveal { opacity: 0; transform: translateY(28px); transition: opacity 0.6s cubic-bezier(.22,1,.36,1), transform 0.6s cubic-bezier(.22,1,.36,1); }
        .reveal.visible { opacity: 1; transform: none; }
        .reveal-delay-1 { transition-delay: 0.08s; }
        .reveal-delay-2 { transition-delay: 0.16s; }
        .reveal-delay-3 { transition-delay: 0.24s; }
        .about-eyebrow {
          display: inline-flex; align-items: center; gap: 6px;
          background: #f4f4f4; color: #000;
          border: 1px solid #d4d4d4;
          border-radius: 99px; padding: 5px 14px;
          font-size: 13px; font-weight: 500;
          margin-bottom: 24px;
          letter-spacing: 0.02em;
        }
        .about-hero h1 {
          font-family: 'Bricolage Grotesque', sans-serif;
          font-size: clamp(38px, 6vw, 68px);
          font-weight: 800;
          color: #000;
          line-height: 1.05;
          letter-spacing: -0.03em;
          margin: 0 0 20px;
          max-width: 720px;
          margin-left: auto; margin-right: auto;
        }
        .about-hero h1 span {
          color: #000;
          text-decoration-thickness: 3px;
        }
        .about-hero p {
          color: #555; font-size: 17px; max-width: 560px;
          margin: 0 auto; line-height: 1.7; font-weight: 300;
        }

        /* STATS BAND */
        .stats-band {
          background: #000;
          padding: 40px 24px;
          display: flex; flex-wrap: wrap; justify-content: center; gap: 0;
        }
        .stat-item {
          display: flex; flex-direction: column; align-items: center;
          padding: 16px 40px;
          border-right: 1px solid rgba(255,255,255,0.1);
          gap: 6px;
          transition: transform 0.2s ease;
        }
        .stat-item:hover { transform: translateY(-2px); }
        .stat-item:last-child { border-right: none; }
        @media (max-width: 600px) {
          .stat-item { flex: 1 1 45%; border-right: none; border-bottom: 1px solid rgba(255,255,255,0.1); padding: 16px 20px; }
          .stat-item:nth-last-child(-n+2) { border-bottom: none; }
        }
        .stat-icon { color: #fff; margin-bottom: 4px; opacity: 0.7; }
        .stat-value {
          font-family: 'Bricolage Grotesque', sans-serif;
          font-size: 32px; font-weight: 800;
          color: #fff; line-height: 1;
          letter-spacing: -0.02em;
        }
        .stat-label { font-size: 13px; color: #888; font-weight: 400; }

        /* SECTIONS */
        .section { max-width: 1100px; margin: 0 auto; padding: 72px 24px; }
        .section-label {
          font-size: 12px; font-weight: 600; letter-spacing: 0.1em;
          text-transform: uppercase; color: #000;
          margin-bottom: 12px;
          border-left: 3px solid #000;
          padding-left: 10px;
        }
        .section h2 {
          font-family: 'Bricolage Grotesque', sans-serif;
          font-size: clamp(28px, 4vw, 44px);
          font-weight: 800; color: #000;
          line-height: 1.1; margin: 0 0 16px;
          letter-spacing: -0.025em;
        }
        .section-sub { color: #555; font-size: 16px; line-height: 1.7; max-width: 520px; margin: 0 0 48px; font-weight: 300; }

        /* FEATURE CARDS */
        .feature-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 20px; }
        .feature-card {
          background: #fff;
          border: 1.5px solid #e5e5e5;
          border-radius: 20px;
          padding: 28px;
          transition: box-shadow 0.2s, transform 0.2s, border-color 0.2s;
        }
        .feature-card:hover { box-shadow: 0 12px 32px rgba(0,0,0,0.1); transform: translateY(-3px); border-color: #000; }
        .feature-icon-wrap {
          width: 52px; height: 52px;
          border-radius: 14px;
          background: #f4f4f4;
          border: 1.5px solid #e5e5e5;
          display: flex; align-items: center; justify-content: center;
          margin-bottom: 18px;
          color: #000;
        }
        .feature-card h3 {
          font-family: 'Bricolage Grotesque', sans-serif;
          font-size: 18px; font-weight: 700;
          color: #000; margin: 0 0 10px;
          letter-spacing: -0.01em;
        }
        .feature-card p { color: #555; font-size: 14.5px; line-height: 1.65; margin: 0; font-weight: 300; }

        /* STORY SECTION */
        .story-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: center; }
        @media (max-width: 768px) { .story-grid { grid-template-columns: 1fr; gap: 40px; } }
        .story-visual {
          background: #000;
          border-radius: 24px;
          padding: 44px 36px;
          color: #fff;
        }
        .story-visual h3 {
          font-family: 'Bricolage Grotesque', sans-serif;
          font-size: 26px; font-weight: 800;
          margin: 0 0 6px; color: #fff;
          letter-spacing: -0.02em;
        }
        .story-visual p { font-size: 14px; color: #888; margin: 0 0 28px; }
        .story-point {
          display: flex; align-items: flex-start; gap: 12px;
          margin-bottom: 14px; font-size: 14px; color: #ccc; line-height: 1.5;
        }
        .story-point-dot {
          width: 20px; height: 20px; border-radius: 50%;
          background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.2);
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0; margin-top: 1px;
        }
        .story-text h2 { margin-bottom: 10px; }
        .story-text .section-sub { margin-bottom: 24px; }
        .cta-link {
          display: inline-flex; align-items: center; gap: 8px;
          background: #000; color: #fff;
          border-radius: 10px; padding: 12px 22px;
          font-size: 14px; font-weight: 500;
          text-decoration: none;
          transition: background 0.15s, transform 0.15s;
        }
        .cta-link:hover { background: #222; transform: translateY(-1px); }

        /* TEAM */
        .team-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 20px; }
        .team-card {
          background: #fff;
          border: 1.5px solid #e5e5e5;
          border-radius: 16px;
          padding: 28px 24px;
          display: flex; flex-direction: column; align-items: center;
          text-align: center;
          transition: box-shadow 0.2s, border-color 0.2s;
        }
        .team-card:hover { box-shadow: 0 8px 24px rgba(0,0,0,0.1); border-color: #000; }
        .avatar {
          width: 60px; height: 60px; border-radius: 50%;
          background: #000;
          display: flex; align-items: center; justify-content: center;
          font-family: 'Bricolage Grotesque', sans-serif;
          font-weight: 800; font-size: 18px; color: #fff;
          margin-bottom: 14px;
          letter-spacing: 0.5px;
        }
        .team-name {
          font-family: 'Bricolage Grotesque', sans-serif;
          font-size: 16px; font-weight: 700;
          color: #000; margin: 0 0 4px;
          letter-spacing: -0.01em;
        }
        .team-role { font-size: 13px; color: #666; }

        /* DIVIDER */
        .divider { border: none; border-top: 1px solid #e5e5e5; margin: 0; }

        /* BOTTOM CTA */
        .bottom-cta {
          background: #000;
          padding: 64px 24px;
          text-align: center;
        }
        .bottom-cta h2 {
          font-family: 'Bricolage Grotesque', sans-serif;
          font-size: clamp(26px, 4vw, 42px);
          font-weight: 800; color: #fff; margin: 0 0 14px;
          letter-spacing: -0.025em;
        }
        .bottom-cta p { color: #888; font-size: 16px; margin: 0 0 32px; font-weight: 300; }
        .cta-row { display: flex; flex-wrap: wrap; gap: 14px; justify-content: center; }
        .btn-white {
          background: #fff; color: #000;
          border: none; border-radius: 10px;
          padding: 13px 26px; font-size: 15px; font-weight: 600;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer; text-decoration: none;
          display: inline-flex; align-items: center; gap: 8px;
          transition: transform 0.15s, box-shadow 0.15s;
        }
        .btn-white:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(255,255,255,0.15); }
        .btn-outline {
          background: transparent; color: #fff;
          border: 1.5px solid rgba(255,255,255,0.3); border-radius: 10px;
          padding: 13px 26px; font-size: 15px; font-weight: 500;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer; text-decoration: none;
          transition: background 0.15s, border-color 0.15s;
        }
        .btn-outline:hover { background: rgba(255,255,255,0.08); border-color: rgba(255,255,255,0.6); }
      `}</style>

      <div className="about-root" data-testid="about-page">

        {/* ── HERO ── */}
        <div className="about-hero">
          <div className="about-hero-dot-grid" />
          <div className="about-eyebrow">
            <Star size={12} /> India's most trusted local job platform
          </div>
          <h1>Jobs you can trust,<br /><span>near where you live</span></h1>
          <p>
            Job Dew connects local businesses and job seekers across every Indian city.
            Admin-verified listings. Zero fees. One tap to apply.
          </p>
        </div>

        {/* ── STATS BAND ── */}
        <div className="stats-band" ref={statsReveal.ref}>
          {STATS.map((s, i) => (
            <div className={`stat-item reveal reveal-delay-${Math.min(i + 1, 3)} ${statsReveal.visible ? "visible" : ""}`} key={s.label}>
              <div className="stat-icon">{s.icon}</div>
              <div className="stat-value">{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        {/* ── FEATURES ── */}
        <div className="section" ref={featuresReveal.ref}>
          <div className="section-label">What sets us apart</div>
          <h2>Built different,<br />for Indian job seekers</h2>
          <p className="section-sub">
            Most job portals cater to metros and corporates. We built Job Dew for everyone — the gold jeweller in Rajkot, the warehouse in Pune, the café in Mysuru.
          </p>
          <div className="feature-grid">
            {FEATURES.map((f, i) => (
              <div className={`feature-card reveal reveal-delay-${Math.min(i + 1, 3)} ${featuresReveal.visible ? "visible" : ""}`} key={f.title}>
                <div className="feature-icon-wrap">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── App install promo ── */}
        <div className="section" style={{ paddingTop: 0 }}>
          <PwaPromoBanner />
        </div>

        <hr className="divider" />

        {/* ── STORY ── */}
        <div className="section" ref={storyReveal.ref}>
          <div className="story-grid">
            <div className={`story-visual reveal ${storyReveal.visible ? "visible" : ""}`}>
              <h3>Our story</h3>
              <p>From an idea in 2022 to millions of connections</p>
              {STORY_POINTS.map((pt, i) => (
                <div className="story-point" key={i}>
                  <div className="story-point-dot">
                    <CheckCircle size={12} color="#fff" />
                  </div>
                  {pt}
                </div>
              ))}
            </div>
            <div className={`story-text reveal reveal-delay-2 ${storyReveal.visible ? "visible" : ""}`}>
              <div className="section-label">Our mission</div>
              <h2>Work should be easy to find, anywhere in India</h2>
              <p className="section-sub">
                We believe geography shouldn't limit opportunity. Whether you're in a metro or a market town, a verified, relevant job should be one click away.
              </p>
              <a href="/jobs" className="cta-link">
                Browse verified jobs <ArrowRight size={15} />
              </a>
            </div>
          </div>
        </div>

        <hr className="divider" />

        {/* ── TEAM ── */}
        {/* <div className="section" style={{ paddingBottom: "72px" }}>
          <div className="section-label">The people behind it</div>
          <h2>Meet the team</h2>
          <p className="section-sub">
            A small, scrappy crew obsessed with making local hiring faster and more human.
          </p>
          <div className="team-grid">
            {TEAM.map((m) => (
              <div className="team-card" key={m.name}>
                <div className="avatar">{m.initials}</div>
                <div className="team-name">{m.name}</div>
                <div className="team-role">{m.role}</div>
              </div>
            ))}
          </div>
        </div> */}

        {/* ── BOTTOM CTA ── */}
        <div className={`bottom-cta reveal ${ctaReveal.visible ? "visible" : ""}`} ref={ctaReveal.ref}>
          <h2>Ready to find your next opportunity?</h2>
          <p>Join over a million job seekers who found work through Job Dew.</p>
          <div className="cta-row">
            <a href="/jobs" className="btn-white">
              Browse Jobs <ArrowRight size={16} />
            </a>
            <a href="/register" className="btn-outline">Post a Job — It's Free</a>
          </div>
        </div>

      </div>
    </>
  );
}